import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import { SubFamily, SubFamilyListResponse, SubFamilyCreate, SubFamilyUpdate } from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.ts';

const PAGE_SIZE = 10;

interface UseSubFamiliesResult {
  subFamilies: SubFamily[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchSubFamilies: (page: number) => Promise<void>; // Changed to required page argument
  handlePageChange: (page: number) => void;
  createSubFamily: (data: SubFamilyCreate) => Promise<boolean>;
  updateSubFamily: (id: number, data: SubFamilyUpdate) => Promise<boolean>;
  deleteSubFamily: (id: number) => Promise<boolean>;
}

export const useSubFamilies = (): UseSubFamiliesResult => {
  const [subFamilies, setSubFamilies] = useState<SubFamily[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubFamilies = useCallback(async (page: number) => { // Made page a required argument
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<SubFamilyListResponse>(
        `/subfamilies?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // ADDED: Bypass cache for pagination
      );
      setSubFamilies(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
      // REMOVED: setCurrentPage(response.pagination.page);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch subfamilies: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching subfamilies.');
      }
      setSubFamilies([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []); // Removed currentPage from dependencies

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchSubFamilies(currentPage);
  }, [currentPage, fetchSubFamilies]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createSubFamily = async (data: SubFamilyCreate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<SubFamily>('/subfamilies', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await fetchSubFamilies(1); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create subfamily: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateSubFamily = async (id: number, data: SubFamilyUpdate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<SubFamily>(`/subfamilies/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      await fetchSubFamilies(currentPage); // Re-fetch current page to show updated data // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update subfamily: ${err.message}` : 'An unknown error occurred during update.');
      return false
    }
  };

  const deleteSubFamily = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete SubFamily ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/subfamilies/${id}`, { method: 'DELETE' });
      const pageToFetch = subFamilies.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchSubFamilies(pageToFetch); // Re-fetch, potentially moving back a page // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete subfamily: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    subFamilies,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchSubFamilies,
    handlePageChange,
    createSubFamily,
    updateSubFamily,
    deleteSubFamily,
  };
};